import React, { memo, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { Colors } from 'context';
import { CareerIcon } from 'atoms/CareerIcon/CareerIcon';
import { SkillIcon } from 'atoms/SkillIcon/SkillIcon';
import { TrackIcon } from 'atoms/TrackIcon/TrackIcon';
import { careerType } from 'services/careerType';

import { resolveClusterIcon } from '../model/clusterIcon';
import { ISkillGraphIndex, clusterProgress, clusterProgressText } from '../model/skillGraphModel';
import { levelPhrase } from '../model/skillLevelLabel';
import { SKILL_STATUS_LOOK } from '../skillStatus';

/**
 * Семантическое дерево карты навыков (ТЗ 0070, раздел доступности).
 *
 * Это НЕ запасной вариант и не упрощённая версия. На мобильном оно основное —
 * канвас, ужатый до трёхсот девяноста пикселей, не читается ни при каком
 * масштабе, — а на десктопе доступно кнопкой «Widok listy» и заодно закрывает
 * зум браузера на 200 %.
 *
 * 🔴 Рендерить дерево и канвас ОДНОВРЕМЕННО нельзя. Два DOM одного содержимого
 * означают, что скринридер прочитает карту дважды подряд, и человек решит, что
 * навыков вдвое больше.
 *
 * 🔴 Roving tabindex: tabIndex = 0 ровно у одного элемента. Иначе Tab
 * проходит шестьдесят остановок подряд, и выйти с карты клавиатурой быстрее
 * перезагрузкой страницы.
 *
 * 🔴 aria-label навыка несёт ИМЯ + СТАТУС + ПАРУ УРОВНЕЙ. Требование ТЗ
 * «скринридер озвучивает название, статус и требуемый уровень» выполняется
 * одним атрибутом, а не тремя отдельными узлами, которые он прочитает
 * вперемешку с разметкой.
 */

interface ISkillGraphTree {
  index: ISkillGraphIndex;
  selected: string | null;
  onSelect: (nodeId: string) => void;
  /**
   * Раскрытые группы — те же, что на холсте (ТЗ 0071, п. 6.1). Не задано или
   * `null` — раскрыто всё.
   *
   * 🔴 Правило одно на оба вида: на телефоне список — основной вид, и без
   * свёртки стартовый экран был бы лентой из шестидесяти строк, где
   * рекомендуемый навык стоит сороковым.
   */
  expanded?: string[] | null;
  /** Раскрыть свёрнутую группу. Без обработчика группы не сворачиваются. */
  onExpand?: (code: string) => void;
}

export const SkillGraphTree = memo(
  ({ index, selected, onSelect, expanded = null, onExpand }: ISkillGraphTree) => {
    const { t } = useTranslation();
    const rootRef = useRef<HTMLUListElement>(null);
    const isOpen = (code: string) => !onExpand || expanded === null || expanded.includes(code);

    // 🔴 Остановка табуляции по умолчанию — ПЕРВЫЙ ВИДИМЫЙ УЗЕЛ, а не первая
    // колонка. Сравнение с кодом колонки не совпадало ни с одним `node.id`, и у
    // дерева без выбранного навыка не было ни одной остановки вовсе: войти в
    // него с клавиатуры было невозможно. Узел свёрнутой группы остановкой быть
    // не может — его нет в разметке.
    const firstNodeId =
      index.clusters.reduce<string | null>(
        (found, cluster) =>
          found ||
          (isOpen(cluster.code) ? (index.nodesOf.get(cluster.code) || [])[0]?.id : null) ||
          null,
        null,
      ) || '';
    const focusable = selected || firstNodeId;

    const onKeyDown = useCallback(
      (event: React.KeyboardEvent<HTMLLIElement>, nodeId: string) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onSelect(nodeId);
          return;
        }
        if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
        event.preventDefault();
        // Ходим по ВИДИМЫМ элементам дерева в порядке DOM: он совпадает с тем,
        // что человек видит, а обход по модели — нет.
        const items = Array.from(
          rootRef.current?.querySelectorAll<HTMLElement>('[role="treeitem"]') || [],
        );
        const at = items.findIndex((item) => item.dataset.nodeId === nodeId);
        const to = event.key === 'ArrowDown' ? at + 1 : at - 1;
        items[to]?.focus();
      },
      [onSelect],
    );

    return (
      <Root ref={rootRef} role="tree" aria-label={t('careerCenter.map.treeLabel')}>
        <li role="treeitem" aria-level={1} aria-expanded="true" aria-selected={false}>
          <TrackTitle>
            <TrackIcon icon={index.trackIcon} size={28} />
            {index.trackTitle}
          </TrackTitle>
          <ul role="group">
            {index.clusters.map((cluster) => {
              const nodes = index.nodesOf.get(cluster.code) || [];
              const progress = clusterProgress(index, cluster.code);
              const open = isOpen(cluster.code);
              const title = (
                <>
                  <TrackIcon icon={resolveClusterIcon(cluster)} size={24} />
                  {cluster.title || t('careerCenter.map.otherCluster')}
                  <Counter>
                    {t(clusterProgressText(progress).key, clusterProgressText(progress).vars)}
                  </Counter>
                </>
              );
              return (
                <li
                  key={cluster.code}
                  role="treeitem"
                  aria-level={2}
                  aria-expanded={open}
                  data-cluster={cluster.code}>
                  {/* Свёрнутая группа — кнопка во всю строку: раскрыть её тем же
                    жестом, что и на холсте, и с зоной нажатия в 44 px. */}
                  {open ? (
                    <ClusterTitle>{title}</ClusterTitle>
                  ) : (
                    <ClusterToggle
                      type="button"
                      aria-expanded={false}
                      onClick={() => onExpand?.(cluster.code)}>
                      {title}
                    </ClusterToggle>
                  )}
                  {open && (
                    <ul role="group">
                      {nodes.map((node) => {
                        const look = SKILL_STATUS_LOOK[node.status];
                        const status = t(look.labelKey);
                        return (
                          <Item
                            key={node.id}
                            role="treeitem"
                            aria-level={3}
                            aria-selected={selected === node.id}
                            aria-label={t('careerCenter.map.nodeLabel', {
                              name: node.name,
                              status,
                              levels: t(levelPhrase(node).key, levelPhrase(node).vars),
                            })}
                            data-node-id={node.id}
                            tabIndex={focusable === node.id ? 0 : -1}
                            $accent={look.accent}
                            $selected={selected === node.id}
                            onClick={() => onSelect(node.id)}
                            onKeyDown={(event) => onKeyDown(event, node.id)}>
                            {/* 🔴 Слева стоит знак ТЕХНОЛОГИИ, как на холсте:
                            левая полка отвечает на «что это», и список обязан
                            читаться так же, как карта. Знак статуса переехал к
                            слову статуса — он принадлежит ему, а не полке; два
                            значка подряд слева были бы двумя разными осями в
                            одном месте. */}
                            <SkillIcon
                              slug={node.slug}
                              name={node.name}
                              category={node.category}
                              iconUrl={node.iconUrl}
                              size={20}
                            />
                            <Name>{node.name}</Name>
                            {/* Слово рядом с цветом и значком: третий канал из
                            четырёх, и единственный, который читается вслух. */}
                            <Status $accent={look.accent}>
                              <CareerIcon name={look.icon} size={16} />
                              {status}
                            </Status>
                            <Levels>{t(levelPhrase(node).key, levelPhrase(node).vars)}</Levels>
                          </Item>
                        );
                      })}
                    </ul>
                  )}
                </li>
              );
            })}
          </ul>
        </li>
      </Root>
    );
  },
);

const Root = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;

  ul {
    list-style: none;
    margin: 0;
    padding: 0 0 0 16px;
  }
`;

const TrackTitle = styled.div`
  ${careerType.h3()};
  color: ${Colors.black};
  padding: 8px 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ClusterTitle = styled.div`
  ${careerType.bodyStrong()};
  color: ${Colors.black};
  padding: 12px 0 4px;
  display: flex;
  /* center, а не baseline: кружок значка на базовой линии «висит». */
  align-items: center;
  gap: 8px;
`;

const ClusterToggle = styled.button`
  ${careerType.bodyStrong()};
  color: ${Colors.black};
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  min-height: 44px;
  margin: 4px 0;
  padding: 8px 12px;
  border: 1px solid ${Colors.system_gray};
  border-radius: 8px;
  background: ${Colors.white};
  text-align: left;
  cursor: pointer;

  &:hover {
    background: ${Colors.bg_gray};
  }
`;

const Counter = styled.span`
  ${careerType.meta()};
  color: ${Colors.gray_dark};
`;

const Item = styled.li<{ $accent: string; $selected: boolean }>`
  display: flex;
  align-items: center;
  /* 8, а не 12: в строке теперь два значка, и на телефоне это основной вид. */
  gap: 8px;
  /* Минимальная зона нажатия из ТЗ — не вкусовщина, а требование. */
  min-height: 44px;
  padding: 8px 12px;
  border-left: 3px solid ${({ $accent }) => $accent};
  border-radius: 8px;
  cursor: pointer;
  color: ${({ $accent }) => $accent};
  background: ${({ $selected }) => ($selected ? Colors.bg_gray : 'transparent')};

  &:hover {
    background: ${Colors.bg_gray};
  }
`;

const Name = styled.span`
  ${careerType.body()};
  color: ${Colors.black};
  flex: 1;
`;

const Status = styled.span<{ $accent: string }>`
  ${careerType.meta()};
  color: ${({ $accent }) => $accent};
  display: inline-flex;
  align-items: center;
  gap: 4px;
  white-space: nowrap;
`;

const Levels = styled.span`
  ${careerType.meta()};
  color: ${Colors.gray_dark};
  white-space: nowrap;
`;
